import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import { DateInvioAtto } from "src/app/models/date-invio-atto";
import { DateInvioAttoService } from "src/app/services/date-invio-atto.service";

export interface DateInvioAttoDialogData {
  incaricoId: number;
  incaricoLabel?: string;
  onUpdate?: (count: number, earliest: moment.Moment | null) => void;
}

@Component({
  selector: "app-date-invio-atto-history-dialog",
  templateUrl: "./date-invio-atto-history-dialog.component.html",
  styleUrls: ["./date-invio-atto-history-dialog.component.scss"],
})
export class DateInvioAttoHistoryDialogComponent implements OnInit {
  entries: DateInvioAtto[] = [];
  loading = false;
  error: string | null = null;
  private isClosing = false;
  private hasMutated = false;

  constructor(
    private dialogRef: MatDialogRef<DateInvioAttoHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DateInvioAttoDialogData,
    private dateInvioAttoService: DateInvioAttoService,
    private toastr: ToastrService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadEntries();

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.closeWithResult(false));
    this.dialogRef
      .keydownEvents()
      .subscribe((event: KeyboardEvent) => {
        if (event.key === "Escape") {
          this.closeWithResult(false);
        }
      });
  }

  trackByEntryId(_index: number, item: DateInvioAtto): number {
    return item.id;
  }

  async loadEntries(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.entries = await this.dateInvioAttoService.listByIncarico(
        this.data.incaricoId
      );
    } catch (error) {
      console.error("Errore caricamento date invio atto:", error);
      this.error = "Errore durante il caricamento delle date invio atto.";
    } finally {
      this.loading = false;
    }
  }

  async remove(entry: DateInvioAtto): Promise<void> {
    if (
      !window.confirm(
        "Sei sicuro di voler eliminare questa data di invio atto?"
      )
    ) {
      return;
    }

    try {
      await this.dateInvioAttoService.delete(entry);
      this.entries = this.entries.filter((item) => item.id !== entry.id);
      this.hasMutated = true;
      this.notifyCount();
      this.toastr.success("Data invio atto rimossa.");
    } catch (error) {
      console.error("Errore eliminazione data invio atto:", error);
      this.toastr.error("Impossibile eliminare la data invio atto.");
    }
  }

  closeWithResult(triggeredByAction = true): void {
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;
    if (!this.hasMutated) {
      this.dialogRef.close();
      return;
    }

    const earliest = this.getEarliestMoment();
    this.dialogRef.close({
      remainingCount: this.entries.length,
      earliestValore: earliest ? earliest.toISOString() : null,
    });
  }

  private notifyCount(): void {
    if (!this.hasMutated) return;
    const earliest = this.getEarliestMoment();
    if (typeof this.data?.onUpdate === "function") {
      this.data.onUpdate(this.entries.length, earliest);
    }
  }

  private getEarliestMoment(): moment.Moment | null {
    const candidates = this.entries
      .map((entry) => entry.valore)
      .filter((value): value is moment.Moment => !!value);

    if (!candidates.length) {
      return null;
    }

    return candidates.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest
    );
  }
}
